/* eslint-disable no-console */
import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../store';
import { callApi } from '../../utils/apiCall';
import { updateError } from './user';

const initialState = {
  isLoading: false,
  error: null,
  list: { results: [], count: 0 },
  filters: {
    limit: 25,
    offset: 0,
    order: 'desc',
    orderBy: 'created_at',
    search: '',
    archived: false,
    date: [],
    treatments: [],
  },
};

const slice = createSlice({
  name: 'patients',
  initialState,
  reducers: {
    startLoading(state) { state.isLoading = true; state.error = null; },
    hasError(state, action) { state.isLoading = false; state.error = action.payload || 'Error'; },
    setList(state, action) { state.isLoading = false; state.list = action.payload || { results: [], count: 0 }; },
    setFilters(state, action) { state.filters = { ...state.filters, ...action.payload }; },
  },
});

export default slice.reducer;

export const { setFilters } = slice.actions;

export function getPatients({ limit, offset, order = 'desc', orderBy = 'created_at', search = '', archived = false, practiceId, treatments = [], date = [] }) {
  return async () => {
    try {
      dispatch(slice.actions.startLoading());
      const ordering = order === 'desc' ? `&ordering=-${orderBy}` : `&ordering=${orderBy}`;
      const q = new URLSearchParams();
      q.set('limit', String(limit ?? 25));
      q.set('offset', String(offset ?? 0));
      // Registered patients who have taken treatment (exclude brand-new leads):
      q.set('is_registered_patient', 'true');
      q.set('is_archived', String(archived));
      // Include all statuses except NEW
      q.set('lead_treatments__status__in', 'CLOSED,CONSULTATION_ATTENDED,CONSULTATION_BOOKED,FAILED_TO_ATTEND_CONSULTATION,TREATMENT_STARTED');
      if (practiceId) q.set('practice__in', String(practiceId));
      if (treatments && treatments.length) q.set('lead_treatments__treatment__in', String(treatments));
      if (date && date.length === 2 && date[0] && date[1]) {
        const start = `${date[0]} 00:00:00`; const end = `${date[1]} 23:59:00`;
        q.set('created_at__range', `${start},${end}`);
      }
      let path = `/api/v1/leads-flat/?${q.toString()}${ordering}&fields={id,first_name,last_name,lead_contact,created_at,updated_at,is_archived,is_registered_patient,lead_treatments}`;
      if (search && search.trim()) {
        const extra = q.toString();
        path = `/api/v1/leads-flat/search/?query=${encodeURIComponent(search)}&${extra}${ordering}&fields={id,first_name,last_name,lead_contact,created_at,updated_at,is_archived,is_registered_patient,lead_treatments}`;
      }
      const res = await callApi(path);
      if (res?.ok || res?.status === 200) {
        dispatch(slice.actions.setList(res.data));
      } else {
        dispatch(slice.actions.hasError(res?.detail || 'Failed to load patients'));
        dispatch(updateError({ message: 'Failed to load patients', success: false }));
      }
    } catch (e) {
      dispatch(slice.actions.hasError(e?.message || 'Failed to load patients'));
      dispatch(updateError({ message: e?.message || 'Failed to load patients', success: false }));
    }
  };
}
